import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtDT } from "@/constants";
import { fmtN, pct, evoCls } from "./common";

const fmtDay = (iso) => (iso || "").slice(0, 10).split("-").reverse().join("/");
const shortUrl = (u) => (u || "").replace("https://www.photobooth54.fr", "") || "/";
const REMINDER_STATE = {
  du: ["Bilan à faire maintenant", "bg-amber-100 text-amber-800"],
  planifie: ["Bilan prévu", "bg-slate-200 text-slate-600"],
  reporte: ["Bilan reporté", "bg-sky-100 text-sky-700"],
  fait: ["Bilan disponible", "bg-emerald-100 text-emerald-700"],
};

function BilanView({ b, testid }) {
  const lines = [
    ["Clics", fmtN(b.before.clicks), fmtN(b.after.clicks), b.evolution.clicks, false],
    ["Impressions", fmtN(b.before.impressions), fmtN(b.after.impressions), b.evolution.impressions, false],
    ["CTR", `${(b.before.ctr * 100).toFixed(1)} %`, `${(b.after.ctr * 100).toFixed(1)} %`, b.evolution.ctr, false],
    ["Position moyenne", b.before.position, b.after.position, b.evolution.position, true],
  ];
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid={testid}>
      <p className="text-xs font-bold text-slate-600">
        Comparaison de périodes de 28 jours — avant : {fmtDay(b.before.start)} → {fmtDay(b.before.end)} · après : {fmtDay(b.after.start)} → {fmtDay(b.after.end)}
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {lines.map(([label, avant, apres, evo, invert]) => (
          <div key={label} className="rounded-lg bg-white border border-slate-200 p-2 text-center">
            <p className="text-[10px] uppercase font-semibold text-slate-400">{label}</p>
            <p className="text-xs text-slate-500 font-mono tabular-nums">{avant} → <strong className="text-slate-800">{apres}</strong></p>
            <p className={`text-xs font-bold font-mono tabular-nums ${evoCls(evo, invert)}`}>{pct(evo)}</p>
          </div>
        ))}
      </div>
      {b.queries?.length > 0 && (
        <div className="space-y-0.5" data-testid={`${testid}-queries`}>
          <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Requêtes de la page</p>
          {b.queries.map((q) => (
            <p key={q.query} className="text-[11px] font-mono tabular-nums text-slate-600">
              « {q.query} » : {q.before ? `${q.before.clicks} clics · ${q.before.impressions} imp. · pos. ${q.before.position}` : "—"} → {q.after ? `${q.after.clicks} clics · ${q.after.impressions} imp. · pos. ${q.after.position}` : "—"}
            </p>
          ))}
        </div>
      )}
      <p className="text-[11px] text-slate-400 italic">{b.note}</p>
    </div>
  );
}

export default function SeoHistory() {
  const [bilan, setBilan] = useState(null);
  const [actions, setActions] = useState(null);
  const [verifying, setVerifying] = useState(null);
  const [reminders, setReminders] = useState(null);
  const [bilanLoading, setBilanLoading] = useState(null);
  const [bilanError, setBilanError] = useState({});
  const [openBilan, setOpenBilan] = useState(null);
  const loadReminders = () => api.get("/seo/reminders").then((r) => {
    setReminders(r.data.reminders);
    window.dispatchEvent(new Event("seo-reminders-changed"));
  }).catch(() => setReminders([]));
  useEffect(() => {
    api.get("/seo/bilan").then((r) => setBilan(r.data)).catch(() => setBilan({ error: true }));
    api.get("/seo/actions").then((r) => setActions(r.data.actions)).catch(() => setActions([]));
    loadReminders();
  }, []);
  const showBilan = async (r) => {
    if (r.state === "fait" && r.bilan?.ok) {
      setOpenBilan(openBilan === r.id ? null : r.id);
      return;
    }
    setBilanLoading(r.id);
    try {
      const res = await api.post(`/seo/actions/${r.id}/bilan`);
      if (res.data.ok) {
        toast.success("Bilan calculé sur deux périodes de 28 jours");
        setBilanError((e) => ({ ...e, [r.id]: null }));
        setOpenBilan(r.id);
        loadReminders();
      } else {
        setBilanError((e) => ({ ...e, [r.id]: res.data.message }));
      }
    } catch (e) {
      setBilanError((er) => ({ ...er, [r.id]: e?.response?.data?.detail || "Bilan impossible pour le moment" }));
    }
    setBilanLoading(null);
  };
  const postpone = async (r) => {
    try {
      await api.post(`/seo/actions/${r.id}/bilan/postpone`, { weeks: 2 });
      toast.success("Rappel reporté de 2 semaines — aucun doublon créé");
      loadReminders();
    } catch { toast.error("Report impossible — réessayez"); }
  };
  const disable = async (r) => {
    try {
      await api.post(`/seo/actions/${r.id}/bilan/disable`);
      toast.success("Rappel désactivé pour cette action");
      loadReminders();
    } catch { toast.error("Désactivation impossible — réessayez"); }
  };
  const verify = async (a, i) => {
    setVerifying(a.id);
    try {
      const r = await api.post(`/seo/actions/${a.id}/verify`);
      setActions((prev) => prev.map((x, j) => (j === i ? { ...x, verification: r.data } : x)));
      if (r.data.published === true) toast.success("Textes bien en ligne sur le site — publication constatée, sans préjuger de la prise en compte par Google");
      else if (r.data.published === false) toast.info("Les textes proposés ne sont pas encore en ligne sur la page — pensez à « Publier » dans Webador");
      else toast.warning("Lecture du site impossible pour le moment — réessayez plus tard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vérification impossible — réessayez");
    }
    setVerifying(null);
  };
  if (!bilan || !actions || !reminders) return <p className="text-sm text-slate-400">Chargement…</p>;
  return (
    <div className="space-y-4" data-testid="seo-history">
      {reminders.length > 0 && (
        <section className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="seo-reminders">
          <p className="font-heading font-extrabold">🔔 Bilans après modification <span className="text-xs font-normal text-slate-400">(rappel automatique 4 semaines après chaque action « Effectuée », décalage Google inclus)</span></p>
          {reminders.map((r, i) => (
            <div key={r.id} className={`rounded-xl border p-3 space-y-2 ${r.state === "du" ? "border-amber-300 bg-amber-50" : "border-slate-200"}`} data-testid={`seo-reminder-${i}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                  <p className="text-sm font-semibold">{r.title}</p>
                  <p className="text-xs text-slate-500 break-all">Page : {shortUrl(r.page)} · modifiée le {fmtDT(r.done_at)}</p>
                  <p className="text-xs text-slate-400">
                    {r.state === "du" && "La période comparable est complète — le bilan peut être calculé."}
                    {r.state === "planifie" && `Bilan prévu le ${fmtDay(r.due_at)}`}
                    {r.state === "reporte" && `Reporté au ${fmtDay(r.due_at)}${r.postponements ? ` (${r.postponements} report${r.postponements > 1 ? "s" : ""})` : ""}`}
                    {r.state === "fait" && "Bilan calculé — consultable ci-dessous."}
                  </p>
                </div>
                <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full ${REMINDER_STATE[r.state][1]}`} data-testid={`seo-reminder-state-${i}`}>
                  {REMINDER_STATE[r.state][0]}
                </span>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button data-testid={`seo-reminder-bilan-${i}`} onClick={() => showBilan(r)} disabled={bilanLoading === r.id}
                  className="rounded-lg bg-slate-900 text-white px-3 py-1.5 text-xs font-bold hover:bg-slate-800 disabled:opacity-50">
                  {bilanLoading === r.id ? "Calcul…" : r.state === "fait" ? (openBilan === r.id ? "Masquer le bilan" : "Revoir le bilan") : "Voir le bilan"}
                </button>
                {r.state !== "fait" && (
                  <>
                    <button data-testid={`seo-reminder-postpone-${i}`} onClick={() => postpone(r)}
                      className="rounded-lg border border-slate-300 text-slate-600 px-3 py-1.5 text-xs font-bold hover:bg-slate-50">
                      Reporter de 2 semaines
                    </button>
                    <button data-testid={`seo-reminder-disable-${i}`} onClick={() => disable(r)}
                      className="rounded-lg border border-rose-300 text-rose-700 px-3 py-1.5 text-xs font-bold hover:bg-rose-50">
                      Désactiver le rappel
                    </button>
                  </>
                )}
              </div>
              {bilanError[r.id] && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid={`seo-reminder-bilan-error-${i}`}>{bilanError[r.id]}</p>
              )}
              {openBilan === r.id && r.bilan?.ok && <BilanView b={r.bilan} testid={`seo-reminder-bilanview-${i}`} />}
            </div>
          ))}
        </section>
      )}
      <section className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="seo-bilan">
        <p className="font-heading font-extrabold">📊 Mon bilan SEO de la semaine <span className="text-xs font-normal text-slate-400">({bilan.start} → {bilan.end})</span></p>
        {!bilan.has_data ? (
          <p className="text-sm text-slate-500">Aucune donnée Google synchronisée pour l'instant — connectez Search Console dans Paramètres.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div><p className="text-xs text-slate-500 uppercase font-semibold">Clics Google</p>
                <p className="font-heading text-xl font-extrabold font-mono tabular-nums">{fmtN(bilan.totals.clicks)} <span className={`text-xs ${evoCls(bilan.evolution.clicks)}`}>{pct(bilan.evolution.clicks)}</span></p></div>
              <div><p className="text-xs text-slate-500 uppercase font-semibold">Impressions</p>
                <p className="font-heading text-xl font-extrabold font-mono tabular-nums">{fmtN(bilan.totals.impressions)} <span className={`text-xs ${evoCls(bilan.evolution.impressions)}`}>{pct(bilan.evolution.impressions)}</span></p></div>
            </div>
            {bilan.pages_up.length > 0 && <p className="text-sm"><span className="font-bold text-emerald-700">Pages en progression :</span> {bilan.pages_up.map((p) => p.replace("https://www.photobooth54.fr", "") || "/").join(" · ")}</p>}
            {bilan.pages_down.length > 0 && <p className="text-sm"><span className="font-bold text-rose-700">Pages en recul :</span> {bilan.pages_down.map((p) => p.replace("https://www.photobooth54.fr", "") || "/").join(" · ")}</p>}
            {bilan.new_queries.length > 0 && <p className="text-sm"><span className="font-bold text-sky-700">Nouveaux mots-clés :</span> {bilan.new_queries.join(" · ")}</p>}
          </>
        )}
        {bilan.priorities?.length > 0 && (
          <div className="border-t border-slate-100 pt-2">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Trois actions prioritaires</p>
            {bilan.priorities.map((p, i) => <p key={i} className="text-sm mt-1">{i + 1}. {p.title}</p>)}
          </div>
        )}
      </section>
      <section className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="seo-actions">
        <p className="px-4 pt-4 text-sm font-bold">Historique des actions réalisées</p>
        {actions.length === 0 && <p className="px-4 pb-4 pt-1 text-sm text-slate-500" data-testid="seo-actions-empty">Aucune action enregistrée — marquez une priorité comme effectuée pour démarrer le suivi.</p>}
        <div className="divide-y divide-slate-100 mt-1">
          {actions.map((a, i) => (
            <div key={a.id} className="px-4 py-3 space-y-1" data-testid={`seo-action-${i}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold">{a.title}</p>
                <p className="text-xs text-slate-400 shrink-0">{fmtDT(a.done_at)}</p>
              </div>
              {a.page && <p className="text-xs text-slate-500 break-all">Page : {a.page.replace("https://www.photobooth54.fr", "") || "/"}</p>}
              {a.commune && <p className="text-xs text-slate-500">Commune : {a.commune}</p>}
              {a.stats_before && <p className="text-xs font-mono tabular-nums text-slate-500">Avant : {Object.entries(a.stats_before).map(([k, v]) => `${k} ${typeof v === "number" ? fmtN(v) : v}`).join(" · ")}</p>}
              {a.texts_before?.title && <p className="text-xs text-slate-400">Ancien titre : {a.texts_before.title}</p>}
              {a.texts_after?.title && <p className="text-xs text-slate-600">Nouveau titre : <strong>{a.texts_after.title}</strong></p>}
              {a.texts_after?.meta && <p className="text-xs text-slate-600">Nouvelle méta : <strong>{a.texts_after.meta}</strong></p>}
              {a.page && a.texts_after && (
                <div className="pt-1 space-y-1">
                  <button data-testid={`seo-action-verify-${i}`} onClick={() => verify(a, i)} disabled={verifying === a.id}
                    className="rounded-lg border border-sky-300 text-sky-700 px-3 py-1.5 text-xs font-bold disabled:opacity-50">
                    {verifying === a.id ? "Vérification…" : "Vérifier sur le site"}
                  </button>
                  {a.verification && (
                    <p className={`text-xs font-bold ${a.verification.published === true ? "text-emerald-700" : "text-amber-700"}`}
                      data-testid={`seo-action-verification-${i}`}>
                      {a.verification.published === true && "✅ Textes bien publiés sur le site"}
                      {a.verification.published === false && "⚠ Textes proposés pas encore en ligne"}
                      {a.verification.published == null && "Lecture du site impossible"}
                      <span className="font-normal text-slate-400"> — vérification du site uniquement, sans lien avec la prise en compte par Google ({fmtDT(a.verification.checked_at)})</span>
                    </p>
                  )}
                </div>
              )}
              {a.apres && (
                <p className={`text-xs font-bold ${a.apres.tendance === "progression" ? "text-emerald-700" : a.apres.tendance === "recul" ? "text-rose-700" : "text-slate-500"}`}
                  data-testid={`seo-action-tendance-${i}`}>
                  Après : {fmtN(a.apres.clicks)} clics · {fmtN(a.apres.impressions)} impressions — {a.apres.tendance} <span className="font-normal text-slate-400">({a.apres.note})</span>
                </p>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
